import React, { Component } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { withTranslation } from 'react-i18next';
import { TOP_LEVEL_NAV, openTopLevelNavSection } from '../leftnav/navAccordion';

var stepDefaults = {
  disableBeacon: true,
  isFixed: true,
};

var getCompleteTourSteps = function(t) {
  return [
    {
      target: '[data-tour="consult-settings"]',
      title: t ? t('Tour.ConsultSettingsTitle') : 'Consult Settings',
      content: t ? t('Tour.ConsultSettingsContent') : 'Configure your audio devices and consultation preferences for video and voice consultations with patients.',
      placement: 'bottom',
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: '[data-tour="kiosk-pagination"]',
      title: t ? t('Tour.PaginationTitle') : 'Enhanced Kiosk Pagination',
      content: t ? t('Tour.PaginationContent') : 'Navigate easily through the kiosk extensions with enhanced pagination controls.',
      placement: 'top',
      disableBeacon: true,
      isFixed: true,
    },
    {
      target: '[data-tour="help-tours-menu"]',
      title: t ? t('Tour.HelpTitle') : 'Help on Demand',
      content: t ? t('Tour.HelpContent') : 'Find these tutorials and more for a refresher anytime.',
      placement: 'right',
      disableBeacon: true,
      isFixed: true,
    },
  ];
};

var getFeaturesTourSteps = function(t) {
  return [
    Object.assign({
      target: '[data-tour="features-nav"]',
      title: t ? t('Tour.FeaturesNavTitle') : 'Solutions',
      content: t ? t('Tour.FeaturesNavContent') : 'Open Solutions anytime to see licensed add-ons for this pharmacy — enabled, locked, or not available at this location.',
      placement: 'right',
    }, stepDefaults),
    Object.assign({
      target: '[data-tour="features-toolbar"]',
      title: t ? t('Tour.FeaturesCatalogTitle') : 'Solutions',
      content: t ? t('Tour.FeaturesCatalogContent') : 'Search and filter here. Enabled features are ready to use. Locked features can be requested with Talk to Expert.',
      placement: 'bottom',
    }, stepDefaults),
    Object.assign({
      target: '[data-tour="features-card"]',
      title: t ? t('Tour.FeaturesCardTitle') : 'Solution cards',
      content: t ? t('Tour.FeaturesCardContent') : 'Each card shows status and a short description. Open it to learn more, or to talk to an expert if it is not enabled yet.',
      placement: 'bottom',
    }, stepDefaults),
    Object.assign({
      target: '[data-tour="features-talk"]',
      title: t ? t('Tour.FeaturesTalkTitle') : 'Talk to Expert',
      content: t ? t('Tour.FeaturesTalkContent') : 'Talk to Expert sends a request to iLocal. Nothing is charged in the portal — enablement happens offline after that conversation.',
      placement: 'top',
    }, stepDefaults),
    Object.assign({
      target: '[data-tour="product-licensing-nav"]',
      title: t ? t('Tour.FeaturesLicensingTitle') : 'Product Licensing',
      content: t ? t('Tour.FeaturesLicensingContent') : 'iLocal members enable or disable a feature for a pharmacy account here after the offline sale. This page never collects payment.',
      placement: 'right',
    }, stepDefaults),
    Object.assign({
      target: '[data-tour="help-tours-menu"]',
      title: t ? t('Tour.FeaturesHelpTitle') : 'Help guide',
      content: t ? t('Tour.FeaturesHelpContent') : 'Replay this tour or open the illustrated Solutions help guide from Help & Tours whenever you need a refresher.',
      placement: 'right',
    }, stepDefaults),
  ];
};

var getTourSteps = function(t, tourName) {
  if (tourName !== 'features') {
    return getCompleteTourSteps(t);
  }
  var steps = getFeaturesTourSteps(t);
  if (typeof document === 'undefined') {
    return steps;
  }
  var optionalTargets = {
    '[data-tour="product-licensing-nav"]': true,
  };
  return steps.filter(function(step) {
    if (!optionalTargets[step.target]) {
      return true;
    }
    return !!document.querySelector(step.target);
  });
};

var getTourStartConfig = function(tourName) {
  if (tourName === 'features') {
    return {
      path: '/home/features',
      pathMatch: '/home/features',
      waitSelector: '[data-tour="features-toolbar"]',
    };
  }
  return {
    path: '/home/prescription/assign',
    pathMatch: '/prescription/assign',
    waitSelector: '[data-tour="kiosk-pagination"]',
  };
};

// Brand color for tour UI (matches joyride primaryColor)
var TOUR_BRAND_COLOR = '#00a7e1';
var TOUR_BRAND_GRADIENT = 'linear-gradient(135deg, #00a7e1 0%, #0077a3 100%)';

// LocalStorage utilities for tour management
var TOUR_STORAGE_KEY = 'rxconnect_tour_completed';

var getAuthToken = function() {
  try {
    var u = JSON.parse(sessionStorage.getItem('user'));
    return (u && u.signInUserSession && u.signInUserSession.idToken && u.signInUserSession.idToken.jwtToken) || '';
  } catch (e) {
    return '';
  }
};

var saveTourWelcomeHiddenPreference = function(userId, onSuccess) {
  var token = getAuthToken();
  fetch(process.env.REACT_APP_BASEURL + 'user/tour-welcome-hidden', {
    method: 'PATCH',
    headers: {
      Authorization: 'Bearer ' + token,
      'Content-Type': 'application/json',
    },
  })
    .then(function(res) { return res.ok ? res.json() : Promise.reject(new Error('Failed to save preference')); })
    .then(function(data) {
      if (data && data.success && onSuccess) onSuccess();
    })
    .catch(function(err) {
      console.error('[TourGuide] Failed to save tour welcome hidden preference', err);
      setTourCompleted(userId);
    });
};

var getTourCompletionStatus = function(userId) {
  try {
    var storedData = localStorage.getItem(TOUR_STORAGE_KEY);
    if (storedData) {
      var completedUsers = JSON.parse(storedData);
      return completedUsers.includes(userId);
    }
    return false;
  } catch (error) {
    console.error('[TourGuide] Error reading tour completion status:', error);
    return false;
  }
};

var setTourCompleted = function(userId) {
  try {
    var storedData = localStorage.getItem(TOUR_STORAGE_KEY);
    var completedUsers = storedData ? JSON.parse(storedData) : [];
    if (!completedUsers.includes(userId)) {
      completedUsers.push(userId);
      localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(completedUsers));
      console.log('[TourGuide] Tour completion saved for user:', userId);
    }
  } catch (error) {
    console.error('[TourGuide] Error saving tour completion status:', error);
  }
};

// Joyride styles configuration
var joyrideStyles = {
  options: {
    primaryColor: TOUR_BRAND_COLOR,
    zIndex: 10000,
    arrowColor: '#fff',
    backgroundColor: '#fff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    textColor: '#333',
  },
  tooltip: {
    borderRadius: 12,
    fontSize: 14,
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    fontSize: 16,
    fontWeight: 600,
  },
  tooltipContent: {
    padding: '10px 0',
  },
  buttonNext: {
    backgroundColor: TOUR_BRAND_COLOR,
    borderRadius: 6,
    fontSize: 14,
  },
  buttonBack: {
    color: '#666',
    marginRight: 10,
  },
  buttonSkip: {
    color: '#999',
  },
  spotlight: {
    borderRadius: 8,
  },
};

// Wrapped component that can accept refs
var TourGuideComponent = class extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showWelcomeModal: false,
      runTour: false,
      stepIndex: 0,
      pendingNavigation: null,
      dontAskAgain: false,
      tourName: 'complete',
      activeSteps: null,
    };
    this.handleJoyrideCallback = this.handleJoyrideCallback.bind(this);
    this.handleStartTour = this.handleStartTour.bind(this);
    this.handleSkipTour = this.handleSkipTour.bind(this);
    this.checkFirstLogin = this.checkFirstLogin.bind(this);
    this.startTourManually = this.startTourManually.bind(this);
    this.beginNamedTour = this.beginNamedTour.bind(this);
  }

  componentDidMount() {
    // Expose instance to parent via ref callback if provided
    if (this.props.onRef) {
      this.props.onRef(this);
    }
    this.checkFirstLogin();
  }

  componentDidUpdate(prevProps, prevState) {
    // Update ref callback if it changed
    if (this.props.onRef && this.props.onRef !== prevProps.onRef) {
      this.props.onRef(this);
    }
    
    if (prevProps.userId !== this.props.userId && this.props.userId) {
      this.checkFirstLogin();
    }
    
    // Close welcome modal if tourWelcomeHidden becomes true
    if (prevProps.tourWelcomeHidden !== this.props.tourWelcomeHidden && this.props.tourWelcomeHidden) {
      this.setState({ showWelcomeModal: false });
    }

    // Lock body scroll when tour runs so backdrop doesn't get pushed by page scroll
    if (prevState.runTour !== this.state.runTour) {
      if (this.state.runTour) {
        this._bodyOverflow = document.body.style.overflow;
        this._htmlOverflow = document.documentElement.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = this._bodyOverflow != null ? this._bodyOverflow : '';
        document.documentElement.style.overflow = this._htmlOverflow != null ? this._htmlOverflow : '';
      }
    }

    // When we reach a Help & Tours step, expand the menu and scroll it into view
    var steps = this.state.activeSteps && this.state.activeSteps.length
      ? this.state.activeSteps
      : getTourSteps(this.props.t, this.state.tourName);
    var lastStep = steps[this.state.stepIndex];
    var isHelpStep = lastStep && lastStep.target === '[data-tour="help-tours-menu"]';
    if (this.state.runTour && isHelpStep && prevState.stepIndex !== this.state.stepIndex) {
      openTopLevelNavSection(TOP_LEVEL_NAV.HELP_TOURS);
      setTimeout(function() {
        var el = document.querySelector('[data-tour="help-tours-menu"]');
        if (el) {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
      }, 150);
    }

    if (
      this.state.runTour &&
      this.state.activeSteps &&
      this.state.activeSteps.length &&
      (prevState.runTour !== this.state.runTour ||
        prevState.stepIndex !== this.state.stepIndex)
    ) {
      var currentStep = this.state.activeSteps[this.state.stepIndex];
      if (currentStep && !this.isTourTargetVisible(currentStep.target)) {
        var skipTo = this.state.stepIndex + 1;
        if (skipTo >= this.state.activeSteps.length) {
          this.setState({
            runTour: false,
            stepIndex: 0,
            tourName: 'complete',
            activeSteps: null,
          });
        } else {
          this.setState({ stepIndex: skipTo });
        }
      }
    }
  }

  componentWillUnmount() {
    // Clear ref on unmount
    if (this.props.onRef) {
      this.props.onRef(null);
    }
    // Restore body scroll if tour was still running
    if (this.state.runTour) {
      document.body.style.overflow = this._bodyOverflow != null ? this._bodyOverflow : '';
      document.documentElement.style.overflow = this._htmlOverflow != null ? this._htmlOverflow : '';
    }
  }

  checkFirstLogin() {
    var userId = this.props.userId;
    var tourWelcomeHidden = this.props.tourWelcomeHidden;
    var self = this;

    if (tourWelcomeHidden || !userId || getTourCompletionStatus(userId)) {
      return;
    }

    setTimeout(function() {
      if (self.props.tourWelcomeHidden) return;
      self.setState({ showWelcomeModal: true });
    }, 1500);
  }

  // Helper function to wait for an element to appear in the DOM
  waitForElement(selector, timeout, callback) {
    var startTime = Date.now();
    var checkInterval = 100; // Check every 100ms
    
    var checkElement = function() {
      var element = document.querySelector(selector);
      if (element) {
        console.log('[TourGuide] Target element found:', selector, {
          element: element,
          visible: element.offsetParent !== null,
          elapsed: Date.now() - startTime + 'ms'
        });
        callback(true, element);
      } else if (Date.now() - startTime < timeout) {
        setTimeout(checkElement, checkInterval);
      } else {
        console.warn('[TourGuide] Target element not found after timeout:', selector, {
          timeout: timeout + 'ms',
          elapsed: Date.now() - startTime + 'ms'
        });
        callback(false, null);
      }
    };
    
    checkElement();
  }

  isTourTargetVisible(selector) {
    if (typeof document === 'undefined' || !selector) {
      return false;
    }
    var el = document.querySelector(selector);
    if (!el) {
      return false;
    }
    var node = el;
    while (node && node !== document.body) {
      if (node instanceof HTMLElement) {
        var style = window.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') {
          return false;
        }
      }
      node = node.parentNode;
    }
    return true;
  }

  resolveRunnableSteps(tourName) {
    var self = this;
    return getTourSteps(this.props.t, tourName).filter(function(step) {
      return self.isTourTargetVisible(step.target);
    });
  }

  handleStartTour() {
    this.beginNamedTour('complete');
  }

  handleSkipTour() {
    var dontAskAgain = this.state.dontAskAgain;
    var userId = this.props.userId;
    var onTourWelcomeHiddenSaved = this.props.onTourWelcomeHiddenSaved;

    this.setState({ showWelcomeModal: false, dontAskAgain: false });

    if (dontAskAgain && userId) {
      saveTourWelcomeHiddenPreference(userId, onTourWelcomeHiddenSaved);
    }
  }

  handleJoyrideCallback(data) {
    var action = data.action;
    var index = data.index;
    var status = data.status;
    var type = data.type;
    var userId = this.props.userId;

    if (action === ACTIONS.CLOSE) {
      this.setState({ 
        runTour: false, 
        stepIndex: 0,
        tourName: 'complete',
        activeSteps: null,
      });
      return; // Exit early, don't process further
    }

    if ([STATUS.SKIPPED].includes(status)) {
      this.setState({ 
        runTour: false, 
        stepIndex: 0,
        tourName: 'complete',
        activeSteps: null,
      });
      return; // Exit early, don't process further
    }

    // Handle step navigation (only if not closing, not skipped, and not finished)
    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type) && 
        action !== ACTIONS.CLOSE && 
        status !== STATUS.SKIPPED && 
        status !== STATUS.FINISHED) {
      // Update step index
      var nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      var tourSteps = this.state.activeSteps && this.state.activeSteps.length
        ? this.state.activeSteps
        : getTourSteps(this.props.t, this.state.tourName);
      var nextStep = tourSteps[nextIndex];
      console.log('[TourGuide] Moving to step index:', nextIndex, {
        eventType: type,
        action: action,
        currentIndex: index,
        target: data.step && data.step.target ? data.step.target : 'N/A'
      });
      
      // If target not found, log warning
      if (type === EVENTS.TARGET_NOT_FOUND) {
        console.warn('[TourGuide] Target element not found for step:', {
          stepIndex: index,
          target: data.step && data.step.target ? data.step.target : 'N/A',
          title: data.step && data.step.title ? data.step.title : 'N/A'
        });
      }

      // Expand Help & Tours before Joyride measures the last step
      if (nextStep && nextStep.target === '[data-tour="help-tours-menu"]') {
        var openedHelpTours = openTopLevelNavSection(TOP_LEVEL_NAV.HELP_TOURS);
        var self = this;
        this.setState({ runTour: false });
        setTimeout(function() {
          var el = document.querySelector('[data-tour="help-tours-menu"]');
          if (el) {
            el.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
          self.setState({ runTour: true, stepIndex: nextIndex });
        }, openedHelpTours ? 350 : 50);
        return;
      }
      
      this.setState({ stepIndex: nextIndex });
    }

    // Handle tour finished status
    if ([STATUS.FINISHED].includes(status)) {
      var tourName = this.state.tourName;
      console.log('[TourGuide] Tour finished', { tourName: tourName });
      if (tourName !== 'features') {
        setTourCompleted(userId);
      }
      this.setState({ 
        runTour: false, 
        stepIndex: 0,
        tourName: 'complete',
        activeSteps: null,
      });
    }
  }

  beginNamedTour(tourName) {
    var self = this;
    var navigate = this.props.navigate;
    var config = getTourStartConfig(tourName);
    var currentPath = (window.location.hash || window.location.pathname).toLowerCase();
    var needsNavigation = currentPath.indexOf(config.pathMatch) === -1;

    var startRun = function() {
      window.dispatchEvent(new CustomEvent('ilocalbox:open-nav-drawer'));
      if (tourName === 'features') {
        window.dispatchEvent(new CustomEvent('ilocalbox:open-solutions-nav'));
      }
      self.waitForElement(config.waitSelector, 5000, function() {
        setTimeout(function() {
          var steps = self.resolveRunnableSteps(tourName);
          if (!steps.length) {
            console.warn('[TourGuide] No visible steps for tour', tourName);
            return;
          }
          var first = document.querySelector(steps[0].target);
          if (first && first.scrollIntoView) {
            first.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
          self.setState({
            runTour: true,
            stepIndex: 0,
            tourName: tourName,
            activeSteps: steps,
          });
        }, 400);
      });
    };

    this.setState({
      showWelcomeModal: false,
      runTour: false,
      stepIndex: 0,
      tourName: tourName,
    }, function() {
      if (needsNavigation && navigate) {
        navigate(config.path);
        setTimeout(startRun, 1000);
      } else {
        startRun();
      }
    });
  }

  // Public method to restart tour from Help & Tours menu
  startTourManually(tourName) {
    var name = tourName || 'complete';
    console.log('[TourGuide] startTourManually called', {
      tourName: name,
      currentPath: window.location.hash || window.location.pathname
    });
    this.beginNamedTour(name);
  }

  render() {
    var showWelcomeModal = this.state.showWelcomeModal;
    var runTour = this.state.runTour;
    var stepIndex = this.state.stepIndex;
    var t = this.props.t;
    var steps = this.state.activeSteps && this.state.activeSteps.length
      ? this.state.activeSteps
      : getTourSteps(t, this.state.tourName);

    return (
      <React.Fragment>
        {/* Welcome Modal - Lower z-index initially to appear below consultation modal */}
        <Dialog
          open={showWelcomeModal}
          onClose={this.handleSkipTour}
          maxWidth="sm"
          fullWidth
          style={{
            zIndex: 1200, // Lower than consultation modal (1300) initially
          }}
          PaperProps={{
            style: {
              borderRadius: 16,
              overflow: 'hidden',
              zIndex: 1200,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            },
          }}
          BackdropProps={{
            style: {
              zIndex: 1199,
            },
          }}
        >
          <Box
            style={{
              background: 'linear-gradient(135deg, #00a7e1 0%, #0077a3 100%)',
              color: 'white',
              position: 'relative',
              padding: '32px 24px',
              textAlign: 'center',
            }}
          >
            <IconButton
              onClick={this.handleSkipTour}
              style={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>
            <LocationOnIcon 
              style={{ 
                fontSize: 64, 
                marginBottom: 16,
                color: 'white'
              }} 
            />
            <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: 8 }}>
              {t ? t('Tour.WelcomeTitle') : 'Welcome to iLocalBox!'}
            </Typography>
            <Typography variant="body1" style={{ opacity: 0.9 }}>
              {t ? t('Tour.WelcomeSubtitle') : "Let's take a quick tour to help you get started"}
            </Typography>
          </Box>
          <DialogContent style={{ padding: '32px 24px', textAlign: 'center' }}>
            <Typography variant="body1" color="textSecondary" style={{ marginBottom: 24 }}>
              {t ? t('Tour.WelcomeDescription') : "Discover the new features and enhancements we've added to make your experience better."}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t ? t('Tour.WelcomeDuration') : 'This tour takes about 30 seconds'}
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              py: 2.5,
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
              borderTop: '1px solid',
              borderColor: 'rgba(0, 167, 225, 0.12)',
              backgroundColor: 'rgba(0, 167, 225, 0.03)',
              mt: 0,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.dontAskAgain}
                  onChange={(e) => this.setState({ dontAskAgain: e.target.checked })}
                  size="small"
                  sx={{
                    padding: '4px',
                    color: 'rgba(0, 167, 225, 0.5)',
                    '&.Mui-checked': {
                      color: '#00a7e1',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: 20,
                      borderRadius: '4px',
                      transition: 'all 0.2s ease',
                    },
                    '&:hover': {
                      color: 'rgba(0, 167, 225, 0.8)',
                      backgroundColor: 'rgba(0, 167, 225, 0.06)',
                      borderRadius: '4px',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(0, 0, 0, 0.6)',
                    fontSize: '15px',
                    fontWeight: 500,
                    letterSpacing: '0.01em',
                  }}
                >
                  {t ? t('Tour.DontAskAgain') : "Don't ask me again"}
                </Typography>
              }
              sx={{
                margin: 0,
                userSelect: 'none',
                padding: '6px 12px',
                marginRight: '-12px',
                marginLeft: '-12px',
                borderRadius: '8px',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: 'rgba(0, 167, 225, 0.04)',
                },
              }}
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                onClick={this.handleSkipTour}
                startIcon={<SkipNextIcon sx={{ fontSize: 18 }} />}
                sx={{
                  color: 'text.secondary',
                  textTransform: 'none',
                  fontSize: '15px',
                }}
              >
                {t ? t('Tour.SkipButton') : 'Skip for now'}
              </Button>
              <Button
                onClick={this.handleStartTour}
                variant="contained"
                startIcon={<PlayArrowIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: TOUR_BRAND_GRADIENT,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '15px',
                  boxShadow: '0 2px 8px rgba(0, 167, 225, 0.35)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 167, 225, 0.4)',
                  },
                }}
              >
                {t ? t('Tour.StartButton') : 'Start Tour'}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Joyride Tour */}
        <Joyride
          key={'joyride-' + (this.state.tourName || 'complete')}
          steps={steps}
          run={runTour}
          stepIndex={stepIndex}
          continuous={true}
          showProgress={true}
          showSkipButton={true}
          scrollToFirstStep={true}
          disableOverlayClose={true}
          disableScrolling={true}
          callback={this.handleJoyrideCallback}
          styles={joyrideStyles}
          floaterProps={{
            disableAnimation: true,
          }}
          locale={{
            back: t ? t('Tour.Back') : 'Back',
            close: t ? t('Tour.Close') : 'Close',
            last: t ? t('Tour.Finish') : 'Finish',
            next: t ? t('Tour.Next') : 'Next',
            skip: t ? t('Tour.Skip') : 'Skip',
          }}
        />
      </React.Fragment>
    );
  }
};

// Export the component wrapped with translation
// Note: We'll use a callback ref pattern instead of forwardRef since withTranslation HOC doesn't support refs
var TourGuideWithTranslation = withTranslation()(TourGuideComponent);

export default TourGuideWithTranslation;
export { getTourCompletionStatus, setTourCompleted };



// WEBPACK FOOTER //
// ./src/components/tour/TourGuide.js